import { Stack, Typography } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid-pro';
import { useCallback, useMemo, useState } from 'react';

import { IconButton } from '@/components/form/baseInputs/Button/IconButton';
import { Edit02Icon } from '@/components/icons/Edit02Icon';
import { Card } from '@/components/layout/Card/Card';
import { TableHeaderBar } from '@/components/tables/components/TableHeaderBar/TableHeaderBar';
import { CellContainer } from '@/components/tables/DataTable/components/cells';
import { DataTable } from '@/components/tables/DataTable/DataTable';
import { useCanEditHouseholdData } from '@/modules/authentication/hooks/useCanEditHouseholdData';
import { sumLiabilities } from '@/modules/estate/EstateDirectHoldings/DirectHoldingsGroupContainer/DirectHoldingsGroupContainer.utils';
import { useHouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';
import { LiabilitiesTable_LiabilityFragment } from '@/modules/liabilities/LiabilitiesTable/graphql/LiabilitiesTable.generated';
import { LiabilitiesTableRowData } from '@/modules/liabilities/LiabilitiesTable/LiabilitiesTable.constants';
import {
  mapLiabilitiesToRows,
  useColumns as useBaseColumns,
} from '@/modules/liabilities/LiabilitiesTable/LiabilitiesTable.utils';
import { EditLiabilitySideDrawer } from '@/modules/liabilities/LiabilityModal/EditLiabilitySideDrawer';
import { formatCurrencyNoDecimals } from '@/utils/formatting/currency';

const MAX_ROW_COUNT = 10;
export interface HoldingsGroupLiabilitiesProps {
  liabilities: LiabilitiesTable_LiabilityFragment[];
  refetch: () => void;
}
export function HoldingsGroupLiabilities({
  liabilities,
  refetch,
}: HoldingsGroupLiabilitiesProps) {
  const { householdId } = useHouseholdDetailsContext();
  const canEdit = useCanEditHouseholdData();
  const [editDrawerOpen, setEditDrawerOpen] = useState(false);
  const [editingLiabilityId, setEditingLiabilityId] = useState<string | null>(
    null
  );

  const handleEditClick = useCallback((liabilityId: string) => {
    setEditingLiabilityId(liabilityId);
    setEditDrawerOpen(true);
  }, []);

  const handleDrawerClose = useCallback(() => {
    setEditDrawerOpen(false);
    setEditingLiabilityId(null);
  }, []);

  const handleLiabilityNotFound = useCallback(() => {
    handleDrawerClose();
    refetch();
  }, [handleDrawerClose, refetch]);

  const currentValue = useMemo(
    () => sumLiabilities(liabilities),
    [liabilities]
  );

  // Get base columns without the delete action
  const baseColumns = useBaseColumns({ refetch });

  // Add edit button column
  const columnsWithEdit = useMemo((): GridColDef<LiabilitiesTableRowData>[] => {
    if (!canEdit) {
      return baseColumns;
    }

    // Find the actions column and replace it with our edit button
    const columnsWithoutActions = baseColumns.filter(
      (col) => col.field !== 'actions'
    );

    const editColumn: GridColDef<LiabilitiesTableRowData> = {
      field: 'actions',
      headerName: '',
      width: 50,
      sortable: false,
      disableColumnMenu: true,
      align: 'center',
      headerAlign: 'center',
      renderCell: ({ row }) => (
        <CellContainer align="center">
          <IconButton
            ariaLabel="Edit liability"
            size="xs"
            variant="transparent"
            icon={Edit02Icon}
            onClick={() => handleEditClick(row.id)}
            data-testid="edit-liability-button"
          />
        </CellContainer>
      ),
    };

    return [...columnsWithoutActions, editColumn];
  }, [baseColumns, canEdit, handleEditClick]);

  const rows = useMemo(
    () => mapLiabilitiesToRows(liabilities, householdId!),
    [householdId, liabilities]
  );

  if (liabilities.length === 0) {
    return null;
  }

  return (
    <>
      <Card variant="outlined">
        <Stack
          sx={{
            p: 2,
            borderRadius: '4px',
          }}
        >
          <Stack gap={2}>
            <Stack>
              <Typography variant="title-s">Liabilities</Typography>
              <Typography variant="body-xs">
                {formatCurrencyNoDecimals(currentValue)} total
              </Typography>
            </Stack>
            <Stack>
              <TableHeaderBar header={'Manually tracked liabilities'} />
              <DataTable
                hideFooterPagination={rows.length <= MAX_ROW_COUNT}
                pageSize={MAX_ROW_COUNT}
                pagination={true}
                rows={rows}
                columns={columnsWithEdit}
                getRowHeight={() => 60}
                paginationMode="client"
                noPointer
                rowHoverVariant="transparent"
                sortingMode="client"
                sortingOrder={['asc', 'desc']}
                initialState={{
                  sorting: {
                    sortModel: [{ field: 'currentValue', sort: 'desc' }],
                  },
                }}
              />
            </Stack>
          </Stack>
        </Stack>
      </Card>
      {editingLiabilityId && (
        <EditLiabilitySideDrawer
          isOpen={editDrawerOpen}
          onClose={handleDrawerClose}
          onNotFound={handleLiabilityNotFound}
          liabilityId={editingLiabilityId}
        />
      )}
    </>
  );
}
